import {
  API_PREFIX,
  type ApiToken,
  type ApiTokenCreated,
  apiTokenInputSchema,
  ApiTokenScopes,
  formatDate,
  formatDateTime,
  routes,
} from '@pmai/contracts';
import {
  Alert,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  EmptyState,
  FormDialog,
  PageHeader,
  PanelDialog,
  SelectField,
  StatusBadge,
  TextField,
} from '@pmai/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiMutation, useApiQuery } from '../../shared/api/hooks';
import { applyFieldErrors, bind, useZodForm } from '../../shared/forms/form';
import { useEnumLabel, useErrorText } from '../../shared/i18n';
import { useConfirm } from '../../shared/use-confirm';

/** Where MCP clients connect (FR-MCP-011) — this site's own API. */
const mcpUrl = () => `${window.location.origin}${API_PREFIX}${routes.mcp.path}`;

/**
 * FR-WS-009: the signed-in user's API tokens for MCP in this workspace — create (shown once),
 * see when each was created and last used, revoke.
 */
export function ApiTokensPage() {
  const { t } = useTranslation();
  const label = useEnumLabel();
  const tokens = useApiQuery(routes.apiTokens.list);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<ApiTokenCreated | null>(null);
  const revoke = useConfirm(routes.apiTokens.revoke, {
    invalidates: [routes.apiTokens.list],
    params: (x: ApiToken) => ({ id: x.id }),
  });

  return (
    <>
      <PageHeader
        title={t('apiTokens.title')}
        actions={
          <Button icon="plus" onClick={() => setCreating(true)}>
            {t('apiTokens.create')}
          </Button>
        }
      />
      <div className="flex flex-col gap-6">
        <Card>
          <div className="flex flex-col gap-2 text-sm">
            <p>{t('apiTokens.intro')}</p>
            <p>
              {t('apiTokens.mcpUrl')}{' '}
              <code data-testid="mcp-url" className="rounded bg-canvas px-1.5 py-0.5">
                {mcpUrl()}
              </code>
            </p>
          </div>
        </Card>
        <DataTable
          label={t('apiTokens.title')}
          rows={tokens.data}
          rowKey={(x) => x.id}
          loading={tokens.isPending}
          empty={<EmptyState icon="key-round" title={t('apiTokens.empty')} />}
          columns={[
            {
              key: 'name',
              header: t('apiTokens.name'),
              cell: (x) => <span className="font-medium">{x.name}</span>,
            },
            {
              key: 'scope',
              header: t('apiTokens.scope'),
              cell: (x) => {
                const o = ApiTokenScopes.byCode(x.scope);
                return <StatusBadge tone={o.tone} label={label(o)} />;
              },
            },
            { key: 'hint', header: t('apiTokens.hint'), cell: (x) => <code>{x.hint}…</code> },
            { key: 'created', header: t('apiTokens.created'), cell: (x) => formatDateTime(x.createdAt) },
            {
              key: 'expires',
              header: t('apiTokens.expires'),
              cell: (x) =>
                x.expired ? (
                  <StatusBadge
                    tone="danger"
                    label={t('apiTokens.expired', { date: formatDate(x.expiresOn) })}
                  />
                ) : x.expiresOn ? (
                  formatDate(x.expiresOn)
                ) : (
                  t('apiTokens.noExpiry')
                ),
            },
            {
              key: 'lastUsed',
              header: t('apiTokens.lastUsed'),
              cell: (x) => (x.lastUsedAt ? formatDateTime(x.lastUsedAt) : t('apiTokens.neverUsed')),
            },
            {
              key: 'actions',
              header: '',
              align: 'right',
              cell: (x) => (
                <Button variant="ghost" size="sm" onClick={() => revoke.ask(x)}>
                  {t('apiTokens.revoke')}
                </Button>
              ),
            },
          ]}
        />
      </div>
      <CreateDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(c) => {
          setCreating(false);
          setCreated(c);
        }}
      />
      <CreatedDialog created={created} onClose={() => setCreated(null)} />
      <ConfirmDialog
        {...revoke.dialog}
        title={t('apiTokens.revokeTitle')}
        message={t('apiTokens.revokeText', { name: revoke.target?.name ?? '' })}
        confirmLabel={t('apiTokens.revoke')}
      />
    </>
  );
}

function CreateDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (created: ApiTokenCreated) => void;
}) {
  const { t } = useTranslation();
  const label = useEnumLabel();
  const errorText = useErrorText();
  const form = useZodForm(apiTokenInputSchema, { defaultValues: { name: '', scope: 'read', expiresOn: '' } });
  const create = useApiMutation(routes.apiTokens.create, {
    invalidates: [routes.apiTokens.list],
    onError: (error) => applyFieldErrors(form, error),
  });
  const close = () => {
    form.reset();
    create.reset();
    onClose();
  };
  return (
    <FormDialog
      open={open}
      onOpenChange={(o) => !o && close()}
      title={t('apiTokens.createTitle')}
      submitLabel={t('apiTokens.createSubmit')}
      submitting={create.isPending}
      error={create.error && errorText(create.error)}
      onSubmit={form.handleSubmit((body) =>
        create.mutate(
          { body },
          {
            onSuccess: (c) => {
              form.reset();
              onCreated(c);
            },
          },
        ),
      )}
    >
      <TextField
        label={t('apiTokens.name')}
        required
        hint={t('apiTokens.nameHint')}
        {...bind(form, 'name')}
      />
      <SelectField
        label={t('apiTokens.scope')}
        required
        hint={t('apiTokens.scopeHint')}
        options={ApiTokenScopes.options.map((o) => ({ value: o.code, label: label(o) }))}
        {...bind(form, 'scope')}
      />
      <TextField
        label={t('apiTokens.expires')}
        type="date"
        hint={t('apiTokens.expiresHint')}
        {...bind(form, 'expiresOn')}
      />
    </FormDialog>
  );
}

/** The new token — the only time it is shown (FR-WS-009). */
function CreatedDialog({ created, onClose }: { created: ApiTokenCreated | null; onClose: () => void }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const close = () => {
    setCopied(false);
    onClose();
  };
  return (
    <PanelDialog
      open={created !== null}
      onOpenChange={(o) => !o && close()}
      title={t('apiTokens.createdTitle')}
    >
      <div className="flex flex-col gap-4">
        <Alert tone="warning">{t('apiTokens.onlyOnce')}</Alert>
        <TextField
          label={t('apiTokens.token')}
          value={created?.token ?? ''}
          readOnly
          onFocus={(e) => e.target.select()}
          className="font-mono text-xs"
        />
        <div className="flex items-center gap-3">
          <Button
            icon="copy"
            variant="secondary"
            onClick={() =>
              void navigator.clipboard?.writeText(created?.token ?? '').then(() => setCopied(true))
            }
          >
            {t('apiTokens.copy')}
          </Button>
          {copied && <span className="text-sm text-ink-muted">{t('apiTokens.copied')}</span>}
        </div>
        <p className="text-sm text-ink-muted">{t('apiTokens.howTo', { url: mcpUrl() })}</p>
      </div>
    </PanelDialog>
  );
}
